/**
 * FileManager API client
 *
 * Works with SVAR React FileManager component
 * Backend: /api/v1/filemanager
 */

import apiClient from './client';

const BASE = '/v1/filemanager';

export interface SVARFile {
  id: string;       // Path-based ID like "/Documents/report.pdf"
  name: string;     // Just the filename
  type: 'file' | 'folder';
  size: number;     // Bytes (0 for folders)
  date: Date;       // Last modified date
  lazy?: boolean;   // Folders have lazy=true for lazy loading
  url?: string;     // Direct URL
  purl?: string;    // Preview URL
}

export interface StorageInfo {
  used: number;     // Bytes used
  total: number;    // Total bytes available (0 = unlimited)
}

/**
 * FileManager API endpoints
 * These are designed to work with SVAR FileManager's RestDataProvider
 */
export const filemanagerApi = {
  /**
   * List files in a directory
   */
  listFiles: async (path = '', search?: string, entityId?: string): Promise<SVARFile[]> => {
    const params: Record<string, string> = {};
    if (search) params.text = search;
    if (entityId) params.entityId = entityId;
    const response = await apiClient.get(
      `${BASE}/files/${encodeURIComponent(path)}`,
      { params }
    );
    return response.data.map((f: Omit<SVARFile, 'date'> & { date: string }) => ({
      ...f,
      date: new Date(f.date),
    }));
  },

  /**
   * Create a new folder
   */
  createFolder: async (parentPath: string, name: string): Promise<SVARFile> => {
    const response = await apiClient.post(
      `${BASE}/files/${encodeURIComponent(parentPath)}`,
      { name, type: 'folder' }
    );
    return {
      ...response.data,
      date: new Date(response.data.date),
    };
  },

  /**
   * Rename a file or folder
   */
  rename: async (id: string, newName: string): Promise<SVARFile> => {
    const response = await apiClient.put(
      `${BASE}/files/${encodeURIComponent(id)}`,
      { operation: 'rename', name: newName }
    );
    return {
      ...response.data,
      date: new Date(response.data.date),
    };
  },

  /**
   * Move files/folders to a new location
   */
  move: async (ids: string[], target: string): Promise<void> => {
    await apiClient.put(`${BASE}/files`, {
      operation: 'move',
      ids,
      target,
    });
  },

  /**
   * Copy files/folders to a new location
   */
  copy: async (ids: string[], target: string): Promise<void> => {
    await apiClient.put(`${BASE}/files`, {
      operation: 'copy',
      ids,
      target,
    });
  },

  /**
   * Delete files/folders
   */
  delete: async (ids: string[]): Promise<void> => {
    await apiClient.delete(`${BASE}/files`, { data: { ids } });
  },

  /**
   * Upload a file
   * @param parentPath - The folder to upload into (empty string for root)
   * @param file - The file to upload
   */
  upload: async (parentPath: string, file: File): Promise<SVARFile> => {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post(
      `${BASE}/upload?id=${encodeURIComponent(parentPath)}`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return {
      ...response.data,
      date: new Date(response.data.date),
    };
  },

  /**
   * Get storage usage info
   */
  getInfo: async (): Promise<StorageInfo> => {
    const response = await apiClient.get(`${BASE}/info`);
    return response.data;
  },

  /**
   * Get direct download URL for a file
   * This returns the URL that can be used to download the file
   */
  getDownloadUrl: (id: string): string => {
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}${BASE}/direct?id=${encodeURIComponent(id)}&download=1`;
  },

  /**
   * Get preview URL for a file (inline display)
   */
  getPreviewUrl: (id: string): string => {
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}${BASE}/direct?id=${encodeURIComponent(id)}`;
  },
};
